import { idmQueryKeys } from '@/api/momo/idm/idm-query-keys'
import { updateStatusPageEmails } from '@/api/momo/idm/users'
import { toastError, toastSuccess } from '@/components/toast/toast'
import { useAuthContext } from '@/hooks/use-auth-context'
import { Anchor, Paper, Stack, Switch, Text, Title } from '@mantine/core'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'

/** The card's id; the status page's Subscribe link opens /profile#status-page-emails. */
export const STATUS_PAGE_EMAILS_ID = 'status-page-emails'

// Set per environment in the .env files. Only QA and prod have a status page;
// dev and local point at the QA one.
const STATUS_PAGE_URL =
  process.env.NEXT_PUBLIC_STATUS_PAGE_URL ??
  'https://status.celerityenterprises.io'
const STATUS_PAGE_HOST = new URL(STATUS_PAGE_URL).host

/**
 * DEV-1676: lets the current user turn status page update emails on or off.
 * The setting comes with the current user, so there is nothing to load here.
 */
export function StatusPageEmails() {
  const { account } = useAuthContext()
  const queryClient = useQueryClient()
  const cardRef = useRef<HTMLDivElement>(null)

  // The card mounts after the session loads, which is after the browser's own
  // scroll to the hash has already run, so the deep link scrolls here itself.
  useEffect(() => {
    if (window.location.hash === `#${STATUS_PAGE_EMAILS_ID}`) {
      cardRef.current?.scrollIntoView?.({ block: 'start' })
    }
  }, [])

  const save = useMutation({
    mutationFn: updateStatusPageEmails,
    onSuccess: (user) => {
      queryClient.setQueryData(idmQueryKeys.users.current(), user)
      toastSuccess(
        user.StatusPageEmailsEnabled
          ? 'You will get status page emails.'
          : 'Status page emails are off.'
      )
    },
    onError: () => toastError('Could not save your status page email setting.')
  })

  return (
    <Paper
      id={STATUS_PAGE_EMAILS_ID}
      ref={cardRef}
      p="lg"
      shadow="sm"
      withBorder
      mt="md"
    >
      <Stack gap="sm">
        <Title order={5}>Status Page Emails</Title>
        <Switch
          label="Email me status page updates"
          checked={account?.StatusPageEmailsEnabled ?? false}
          disabled={!account || save.isPending}
          onChange={(event) => save.mutate(event.currentTarget.checked)}
        />
        <Text size="sm" c="dimmed">
          Get an email when{' '}
          <Anchor href={STATUS_PAGE_URL} target="_blank" size="sm">
            {STATUS_PAGE_HOST}
          </Anchor>{' '}
          opens or resolves an incident, or schedules maintenance.
        </Text>
      </Stack>
    </Paper>
  )
}
